import React, { useState, useEffect, useCallback, useMemo } from "react"
import { TAsetAddress } from "../services/applicationAPI"
import { getErrorMessage } from "../types/error"

interface AddressData {
  contactName: string
  contactPhone: string
  city: string
  country: string
  address: string
  details: string
  zipCode: string
  id: string
  extra_information: string
}

interface UpdateAddressModalProps {
  isOpen: boolean
  onClose: () => void
  applicationId: string
  currentAddress?: AddressData
}

const initialAddressState: AddressData = {
  contactName: "",
  contactPhone: "",
  city: "",
  country: "",
  address: "",
  details: "",
  zipCode: "",
  id: "",
  extra_information: "",
}

export const UpdateAddressModal: React.FC<UpdateAddressModalProps> = React.memo(
  ({ isOpen, onClose, applicationId, currentAddress }) => {
    const [address, setAddress] = useState<AddressData>(initialAddressState)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
      if (currentAddress && isOpen) {
        setAddress(currentAddress)
      } else if (!isOpen) {
        setAddress(initialAddressState)
        setError(null)
      }
    }, [currentAddress, isOpen])

    const handleInputChange = useCallback((field: keyof AddressData, value: string) => {
      setAddress((prev) => ({
        ...prev,
        [field]: value,
      }))
    }, [])

    const handleSubmit = useCallback(
      async (e: React.FormEvent) => {
        e.preventDefault()
        setIsLoading(true)
        setError(null)

        try {
          await TAsetAddress(applicationId, address)
          setIsLoading(false)
          onClose()
        } catch (error: unknown) {
          setIsLoading(false)
          setError(getErrorMessage(error) || "Adres güncellenirken bir hata oluştu")
        }
      },
      [applicationId, address, onClose],
    )

    const handleClose = useCallback(() => {
      onClose()
    }, [onClose])

    const formFields = useMemo(
      () => [
        {
          key: "contactName" as keyof AddressData,
          label: "İletişim Kişisi",
          type: "text",
          required: true,
        },
        {
          key: "contactPhone" as keyof AddressData,
          label: "İletişim Telefonu",
          type: "tel",
          required: true,
        },
        { key: "city" as keyof AddressData, label: "Şehir", type: "text", required: true },
        { key: "country" as keyof AddressData, label: "Ülke", type: "text", required: true },
        {
          key: "address" as keyof AddressData,
          label: "Adres",
          type: "textarea",
          required: true,
          rows: 3,
        },
        {
          key: "details" as keyof AddressData,
          label: "Detaylar",
          type: "textarea",
          required: false,
          rows: 2,
        },
        { key: "zipCode" as keyof AddressData, label: "Posta Kodu", type: "text", required: false },
        { key: "id" as keyof AddressData, label: "TC Kimlik No", type: "text", required: false },
        {
          key: "extra_information" as keyof AddressData,
          label: "Ek Bilgiler",
          type: "textarea",
          required: false,
          rows: 2,
        },
      ],
      [],
    )

    if (!isOpen) {
      return null
    }

    return (
      <div
        className="fixed inset-0 bg-gray-900 bg-opacity-75 backdrop-blur-sm flex items-center justify-center z-50"
        onClick={handleClose}
      >
        <div
          className="bg-white p-6 rounded-lg shadow-xl overflow-y-auto overscroll-contain max-h-[90vh] max-w-md w-full space-y-4 relative"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Adres Güncelle</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            {formFields.map((field) => (
              <div key={field.key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {field.label}
                </label>
                {field.type === "textarea" ? (
                  <textarea
                    value={address[field.key]}
                    onChange={(e) => handleInputChange(field.key, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    rows={field.rows}
                    required={field.required}
                  />
                ) : (
                  <input
                    type={field.type}
                    value={address[field.key]}
                    onChange={(e) => handleInputChange(field.key, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    required={field.required}
                  />
                )}
              </div>
            ))}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors text-sm"
                disabled={isLoading}
              >
                İptal
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors disabled:opacity-50 text-sm"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white mr-2"></div>
                    Güncelleniyor...
                  </div>
                ) : (
                  "Güncelle"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  },
)

UpdateAddressModal.displayName = "UpdateAddressModal"
